import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { colors } from "theme/palette";
import { fieldOptionMultipleRetrieve } from "utils/common";

import Box from "components/Box";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";

import FieldTitle from "entities/Application/components/FieldTitle";
import { IApplicationField } from "entities/Application/sdk";
import EditButton from "entities/Field/components/Checkboxfield/EditButton";
import { IFieldOption } from "entities/Package/sdk";

export interface ICheckboxfield {
  field: IApplicationField;
  editable: boolean;
  onUpdate: ({
    field,
    value,
  }: {
    field: IApplicationField;
    value: string | null | undefined;
  }) => void;
}

const Checkboxfield = ({ field, editable, onUpdate }: ICheckboxfield) => {
  const [isOpen, setIsOpen] = useState(false);

  const [value, setValue] = useState<Array<IFieldOption> | null>(
    fieldOptionMultipleRetrieve({
      options: field.options,
      value: _.get(field.values, "[0].value"),
    })
  );

  const handleEditButtonClick = useCallback(() => setIsOpen(true), [setIsOpen]);

  const handleSave = useCallback(
    ({ newValue }: { newValue: Array<IFieldOption> | null }) => {
      onUpdate({
        field,
        value: newValue?.map((option) => option.value).join(",") || null,
      });
      setValue(newValue);
      setIsOpen(false);
    },
    [field, onUpdate, setValue]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setValue(
      fieldOptionMultipleRetrieve({
        options: field.options,
        value: _.get(field.values, "[0].value"),
      })
    );
  }, [setValue, field]);

  const showRequiredTag = useMemo(
    () => editable && !value && field.is_required,
    [editable, value, field.is_required]
  );

  const displayValue = useMemo(() => {
    if (value?.length === 1) {
      return <Box>{value[0].label}</Box>;
    }

    return (
      <ul
        data-testid="field-value"
        style={{
          margin: 0,
        }}
      >
        {value
          ?.map((v) => v.label)
          .map((item) => (
            <li>
              <Text variant="text2">{item}</Text>
            </li>
          ))}
      </ul>
    );
  }, [value]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      padding={0.5}
      sx={{
        borderBottom: `1px solid ${colors.blue20}`,
      }}
    >
      <FieldTitle field={field} />

      <Stack direction="row" spacing={1} alignItems="center" maxWidth="40%">
        {showRequiredTag && <Tag label="required" variant="red" />}
        {value && displayValue}
        {editable && (
          <EditButton
            isOpen={isOpen}
            name={field.name}
            options={field.options}
            initialValue={value}
            onSave={handleSave}
            onCancel={handleCancel}
            onClick={handleEditButtonClick}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default Checkboxfield;
